// src/components/staff-time/StaffReports.jsx
import React, { useState, useEffect } from 'react';
import { Download, Plus, X, Save } from 'lucide-react';
import { getTimeEntries, getAllStaffIncludingPaused } from '../../services/staffService';
import { useLocation } from '../../contexts/LocationContext';
import LocationSwitcher from '../shared/LocationSwitcher';
import { collection, addDoc, updateDoc, doc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { styles } from '../../styles/theme';
import { getPayrollCenter } from '../admin/LocationsManagement';

// ── Helpers ──────────────────────────────────────────────────────────────────

const convertToFirestoreDate = (yyyymmdd) => {
  const [year, month, day] = yyyymmdd.split('-');
  return `${month}-${day}-${year}`;
};

const parseFirestoreDate = (dateStr) => {
  if (!dateStr) return new Date();
  const [month, day, year] = dateStr.split('-');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

const SCHOOL_LOCATIONS = ['bca1', 'bca2', 'bca', 'cascadia', 'other'];

const isSchoolLocation = (location) => {
  if (!location) return false;
  const loc = location.toLowerCase().trim();
  if (!loc || loc === 'center') return false;
  // If it looks like a Firestore document ID (long alphanumeric, no spaces/hyphens) — treat as center
  if (loc.length > 15 && /^[a-z0-9]+$/i.test(loc)) return false;
  return SCHOOL_LOCATIONS.some(s => loc.includes(s));
};

// Same key StaffStatus saves as checkInLocation when a staff member checks in at a location
const locationKeyFromName = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('kirkland')) return 'kirkland';
  if (n.includes('redmond')) return 'redmond';
  if (n.includes('stemtree') || n.includes('center')) return 'center';
  if (n.includes('bca1') || n.includes('campus 1')) return 'bca1';
  if (n.includes('bca2') || n.includes('campus 2')) return 'bca2';
  if (n.includes('cascadia')) return 'cascadia';
  if (n.includes('bca')) return 'bca1';
  return n.replace(/\s+/g, '_');
};

// Map of location key / doc id → payroll center, from each location's "Payroll Charged To" setting
const buildPayrollMap = (locationDocs) => {
  const map = {};
  locationDocs.forEach(loc => {
    const center = getPayrollCenter(loc);
    map[loc.id.toLowerCase()] = center;
    map[locationKeyFromName(loc.name)] = center;
  });
  return map;
};

// Classify a shift by where it was actually clocked in (not where the staff member is assigned).
// Returns { center: 'redmond' | 'kirkland', isSchool }
const classifyEntry = (location, payrollMap) => {
  const loc = (location || '').toLowerCase();
  if (loc.includes('kirkland')) return { center: 'kirkland', isSchool: false };
  if (!loc || loc.includes('redmond') || loc === 'center') return { center: 'redmond', isSchool: false };
  if (payrollMap[loc]) return { center: payrollMap[loc], isSchool: true };
  // Legacy school keys (bca/cascadia/other) and unknown values → Redmond
  return { center: 'redmond', isSchool: isSchoolLocation(loc) };
};

const CENTER_LABELS = { redmond: 'Redmond', kirkland: 'Kirkland' };

// Break rule:
// Break 1 ≤10 min → free
// Break 2 >10 min → deduct break 2
// Break 2 ≤10 min + break 3 exists → deduct break 3
// Returns deduction in hours
const calcBreakDeduction = (breaks = []) => {
  if (!breaks || breaks.length === 0) return 0;
  const b2 = breaks[1];
  const b3 = breaks[2];

  // Break 1 is always free (≤10 min paid rest)
  if (!b2) return 0;

  // Break 2 exists
  const b2Duration = b2.duration || 0; // minutes
  if (b2Duration > 10) {
    return b2Duration / 60; // deduct break 2
  }

  // Break 2 ≤10 min — check for break 3
  if (b3) {
    return (b3.duration || 0) / 60; // deduct break 3
  }

  return 0;
};

// Calculate paid hours for an entry using our break rules
const calcPaidHours = (entry) => {
  if (!entry.totalHours) return 0;

  const deduction = calcBreakDeduction(entry.breaks);

  // School shifts = actual hours worked (cap removed)
  if (isSchoolLocation(entry.checkInLocation)) {
    return Math.max(0, (entry.totalHours || 0) - deduction);
  }
  return Math.max(0, (entry.totalHours || 0) - deduction);
};

// Get bi-weekly pay period dates (Sun–Sat, 2 weeks)
const getBiweeklyDates = (offset = 0) => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - dayOfWeek - (offset * 14));
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfPeriod = new Date(startOfWeek);
  endOfPeriod.setDate(startOfWeek.getDate() + 13);
  return {
    start: startOfWeek.toISOString().split('T')[0],
    end: endOfPeriod.toISOString().split('T')[0],
  };
};

const fmt12 = (timestamp) => {
  if (!timestamp) return '-';
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

// ── Component ─────────────────────────────────────────────────────────────────

const StaffReports = ({ onBack }) => {
  const { currentLocation } = useLocation();
  const [timeEntries, setTimeEntries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' | 'detail' | 'schedule'
  const [schedules, setSchedules] = useState([]); // all schedule entries for the period
  const [payrollMap, setPayrollMap] = useState({}); // location key/id → 'redmond' | 'kirkland'
  const [schoolLocations, setSchoolLocations] = useState([]); // active non-center locations

  const currentPeriod = getBiweeklyDates(0);
  const [startDate, setStartDate] = useState(currentPeriod.start);
  const [endDate, setEndDate] = useState(currentPeriod.end);
  const [selectedStaff, setSelectedStaff] = useState('all');

  // Missed shift modal
  const [showMissedShift, setShowMissedShift] = useState(false);
  const [missedForm, setMissedForm] = useState({
    staffId: '', date: '', location: 'redmond', checkIn: '', checkOut: '', notes: ''
  });
  const [savingMissed, setSavingMissed] = useState(false);

  // Edit entry modal
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ checkOut: '', notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => { loadData(); }, [startDate, endDate, currentLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [entries, staffList, locSnap] = await Promise.all([
        getTimeEntries(convertToFirestoreDate(startDate), convertToFirestoreDate(endDate)),
        getAllStaffIncludingPaused(),
        getDocs(collection(db, 'locations'))
      ]);
      const locDocs = locSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const map = buildPayrollMap(locDocs);
      setPayrollMap(map);
      setSchoolLocations(locDocs.filter(l => l.active && classifyEntry(locationKeyFromName(l.name), map).isSchool));

      // Filter by location — time entries store location in checkInLocation
      const locFilter = (r) => currentLocation === 'all' ||
        classifyEntry(r.checkInLocation, map).center === currentLocation;
      const locEntries = entries.filter(locFilter);
      setTimeEntries(locEntries);

      // Staff store allowedLocations as location doc ids (or legacy keys like 'center').
      // Map each to its payroll center; anyone with no recognizable location defaults to Redmond.
      const staffCenters = (s) => {
        const centers = new Set((s.allowedLocations || []).map(id => classifyEntry(id, map).center));
        if (centers.size === 0) centers.add('redmond');
        return centers;
      };
      // Also include anyone who clocked in at this location during the period
      const staffWithEntries = new Set(locEntries.map(e => e.staffId));
      const locStaff = staffList.filter(s =>
        currentLocation === 'all' || staffCenters(s).has(currentLocation) || staffWithEntries.has(s.id)
      );
      setStaff(locStaff);
      // If the selected staff member isn't in this location's list, fall back to All Staff
      setSelectedStaff(prev => (prev === 'all' || locStaff.some(s => s.id === prev)) ? prev : 'all');

      // Load schedules filtered by location and date range
      const schedSnap = await getDocs(collection(db, 'schedules'));
      const allSchedules = schedSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const start = new Date(startDate);
      const end = new Date(endDate);
      const filtered = allSchedules.filter(s => {
        if (!s.date) return false;
        const parts = s.date.split('-').map(Number);
        const [m, d2] = parts;
        const y = parts[2] < 100 ? 2000 + parts[2] : parts[2]; // handle 2-digit year
        const sd = new Date(y, m - 1, d2);
        if (sd < start || sd > end) return false;
        const schedLoc = (s.location || s.locationId || '').toLowerCase();
        const normLoc = schedLoc.includes('kirkland') ? 'kirkland' : 'redmond';
        if (currentLocation !== 'all' && normLoc !== currentLocation) return false;
        return true;
      });
      setSchedules(filtered);
    } catch (err) {
      console.error('Error loading:', err);
      alert('Failed to load time entries');
    } finally {
      setIsLoading(false);
    }
  };

  const setPeriod = (offset) => {
    const p = getBiweeklyDates(offset);
    setStartDate(p.start);
    setEndDate(p.end);
  };

  // Filter entries
  const filteredEntries = timeEntries.filter(e =>
    selectedStaff === 'all' || e.staffId === selectedStaff
  );

  // ── Payroll Summary ───────────────────────────────────────────────────────
  // Per staff: Redmond hours, Kirkland hours, School hours, total paid
  const payrollSummary = (() => {
    const byStaff = {};
    filteredEntries.forEach(entry => {
      const id = entry.staffId;
      if (!byStaff[id]) {
        byStaff[id] = {
          staffId: id,
          staffName: entry.staffName,
          redmondHours: 0,
          kirklandHours: 0,
          schoolShifts: 0,
          schoolHours: 0,
          totalPaid: 0,
          entries: [],
          flags: [],
        };
      }
      const paid = calcPaidHours(entry);
      const { center, isSchool } = classifyEntry(entry.checkInLocation, payrollMap);
      if (center === 'kirkland') byStaff[id].kirklandHours += paid;
      else byStaff[id].redmondHours += paid;
      // School hours are already counted in their center's payroll above; tracked separately for reference
      if (isSchool) {
        byStaff[id].schoolShifts++;
        byStaff[id].schoolHours += paid;
      }
      byStaff[id].totalPaid += paid;
      byStaff[id].entries.push(entry);

      // Flag issues
      if (!entry.checkOutTime) byStaff[id].flags.push(`${entry.date}: no checkout`);
      if (entry.totalHours > 12) byStaff[id].flags.push(`${entry.date}: ${entry.totalHours.toFixed(1)}h seems long`);
    });

    return Object.values(byStaff).sort((a, b) =>
      a.staffName.localeCompare(b.staffName)
    );
  })();

  const grandTotal = payrollSummary.reduce((s, r) => s + r.totalPaid, 0);
  const sumBy = (field) => payrollSummary.reduce((s, r) => s + r[field], 0);

  // ── Add Missed Shift ──────────────────────────────────────────────────────
  const handleSaveMissedShift = async () => {
    if (!missedForm.staffId || !missedForm.date || !missedForm.checkIn || !missedForm.checkOut) {
      alert('Please fill in staff, date, check-in and check-out times.');
      return;
    }
    setSavingMissed(true);
    try {
      const selectedStaffMember = staff.find(s => s.id === missedForm.staffId);
      const [y, m, d] = missedForm.date.split('-');
      const firestoreDate = `${m}-${d}-${y}`;

      const checkIn = new Date(`${missedForm.date}T${missedForm.checkIn}:00`);
      const checkOut = new Date(`${missedForm.date}T${missedForm.checkOut}:00`);
      const totalHours = (checkOut - checkIn) / (1000 * 60 * 60);

      await addDoc(collection(db, 'time_entries'), {
        staffId: missedForm.staffId,
        staffName: `${selectedStaffMember.firstName} ${selectedStaffMember.lastName}`,
        date: firestoreDate,
        checkInLocation: missedForm.location,
        checkOutLocation: missedForm.location,
        checkInTime: checkIn,
        checkOutTime: checkOut,
        totalHours,
        paidHours: totalHours,
        breaks: [],
        status: 'checked-out',
        addedManually: true,
        notes: missedForm.notes,
        createdAt: serverTimestamp(),
      });

      setShowMissedShift(false);
      setMissedForm({ staffId: '', date: '', location: 'redmond', checkIn: '', checkOut: '', notes: '' });
      await loadData();
      alert('✅ Shift added successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to save shift.');
    } finally {
      setSavingMissed(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.checkOut) { alert('Please enter a checkout time.'); return; }
    setSavingEdit(true);
    try {
      const [y, m, d] = editEntry.date.split('-').length === 3 && editEntry.date.split('-')[2].length === 4
        ? [editEntry.date.split('-')[2], editEntry.date.split('-')[0], editEntry.date.split('-')[1]]
        : [editEntry.date.split('-')[0], editEntry.date.split('-')[1], editEntry.date.split('-')[2]];
      const dateStr = `${y}-${m}-${d}`;
      const checkOutDate = new Date(`${dateStr}T${editForm.checkOut}:00`);
      const checkInDate = editEntry.checkInTime?.toDate ? editEntry.checkInTime.toDate() : new Date(editEntry.checkInTime);
      const totalHours = (checkOutDate - checkInDate) / (1000 * 60 * 60);
      await updateDoc(doc(db, 'time_entries', editEntry.id), {
        checkOutTime: checkOutDate,
        totalHours,
        paidHours: totalHours,
        status: 'checked-out',
        adminEdited: true,
        adminNotes: editForm.notes,
        updatedAt: serverTimestamp(),
      });
      setEditEntry(null);
      await loadData();
    } catch (err) {
      alert('Failed to save edit.');
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const exportPayrollSummary = () => {
    const headers = ['Employee', 'Redmond Hours', 'Kirkland Hours', 'School Shifts', 'School Hours (included in center hours)', 'Total Paid Hours', 'Flags'];
    const rows = payrollSummary.map(r => [
      r.staffName,
      r.redmondHours.toFixed(2),
      r.kirklandHours.toFixed(2),
      r.schoolShifts,
      r.schoolHours.toFixed(2),
      r.totalPaid.toFixed(2),
      r.flags.join('; '),
    ]);
    rows.push([
      'TOTAL',
      sumBy('redmondHours').toFixed(2),
      sumBy('kirklandHours').toFixed(2),
      sumBy('schoolShifts'),
      sumBy('schoolHours').toFixed(2),
      grandTotal.toFixed(2),
      '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `payroll-summary-${startDate}-to-${endDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportDetailedReport = () => {
    const headers = ['Employee','Date','Location','Check In','Check Out','Total Hrs','Break Deduction','Paid Hrs','Flags'];
    const rows = filteredEntries.map(e => {
      const school = isSchoolLocation(e.checkInLocation);
      const deduction = school ? 0 : calcBreakDeduction(e.breaks);
      const paid = calcPaidHours(e);
      const flags = [];
      if (!e.checkOutTime) flags.push('no checkout');
      if (e.addedManually) flags.push('manually added');
      return [
        e.staffName,
        e.date,
        e.checkInLocation || '-',
        fmt12(e.checkInTime),
        fmt12(e.checkOutTime),
        e.totalHours?.toFixed(2) || '0',
        deduction.toFixed(2),
        paid.toFixed(2),
        flags.join('; '),
      ];
    });
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `detailed-report-${startDate}-to-${endDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)', padding: '2rem 0', marginBottom: '2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.2)', border: '2px solid white', color: 'white', padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 'bold', cursor: 'pointer', marginBottom: '1rem' }}>
            ← Back
          </button>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>📊 Time Reports</h1>
          <p style={{ color: 'rgba(255,255,255,0.9)', margin: '0.5rem 0 0 0' }}>Staff hours and payroll summary</p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem 2rem 1rem' }}>

        {/* Pay Period Controls */}
        <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Location</label>
                <LocationSwitcher />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Start</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>End</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Staff</label>
                <select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)} style={styles.input}>
                  <option value="all">All Staff</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Pay Period</label>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  {[['Current', 0], ['Previous', 1], ['-2', 2]].map(([label, offset]) => (
                    <button key={offset} onClick={() => setPeriod(offset)} style={{ padding: '0.4rem 0.75rem', fontSize: '0.78rem', fontWeight: '600', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '2px solid #e5e7eb' }}>
          {[['summary', '💰 Payroll Summary'], ['detail', '🕐 Detail View'], ['schedule', '📋 Schedule Check']].map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '0.6rem 1.25rem', border: 'none', borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
              backgroundColor: 'transparent', color: activeTab === tab ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === tab ? '700' : '400', fontSize: '0.9rem', cursor: 'pointer', marginBottom: '-2px',
            }}>
              {label}
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          <button onClick={exportPayrollSummary} disabled={payrollSummary.length === 0} style={{ ...styles.button, backgroundColor: '#10b981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Download size={16} /> Export Payroll Summary
          </button>
          <button onClick={exportDetailedReport} disabled={filteredEntries.length === 0} style={{ ...styles.button, backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Download size={16} /> Export Detail
          </button>
          <button onClick={() => setShowMissedShift(true)} style={{ ...styles.button, backgroundColor: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
            <Plus size={16} /> Add Missed Shift
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#6b7280' }}>Loading...</div>
        ) : (
          <>
            {/* ── Payroll Summary Tab ── */}
            {activeTab === 'summary' && (
              <div>
                {/* Grand total */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  {[
                    { label: 'Redmond Hours', value: sumBy('redmondHours').toFixed(2), color: '#3b82f6' },
                    { label: 'Kirkland Hours', value: sumBy('kirklandHours').toFixed(2), color: '#0891b2' },
                    { label: 'School Hours (incl. above)', value: sumBy('schoolHours').toFixed(2), color: '#8b5cf6' },
                    { label: 'Total Paid Hours', value: grandTotal.toFixed(2), color: '#10b981' },
                  ].map(s => (
                    <div key={s.label} style={{ ...styles.card, padding: '1.1rem 1.25rem', borderLeft: `4px solid ${s.color}` }}>
                      <div style={{ fontSize: '1.8rem', fontWeight: '800', color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Per-staff summary table */}
                <div style={styles.card}>
                  <h2 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '1rem' }}>
                    QuickBooks Entry — {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </h2>
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
                    Hours are split by where each shift was clocked in, so staff who work at both centers are split automatically.
                    School shifts (BCA, Cascadia, etc.) count toward the center set under “Payroll Charged To” in Locations, and are also shown in the School columns for reference.
                    Break rules are applied to all hours.
                    {currentLocation !== 'all' && <strong> Choose “All Locations” to see the full split for payroll.</strong>}
                  </p>

                  {payrollSummary.length === 0 ? (
                    <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No entries for this period.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700' }}>Employee</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.8rem', fontWeight: '700', color: '#3b82f6' }}>Redmond Hrs</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.8rem', fontWeight: '700', color: '#0891b2' }}>Kirkland Hrs</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: '700', color: '#8b5cf6' }}>School Shifts</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.8rem', fontWeight: '700', color: '#8b5cf6' }}>School Hrs (incl.)</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.8rem', fontWeight: '700', color: '#10b981' }}>Total Paid</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700', color: '#dc2626' }}>Flags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payrollSummary.map((r, i) => (
                          <tr key={r.staffId} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                            <td style={{ padding: '0.85rem 1rem', fontWeight: '600', fontSize: '0.875rem' }}>{r.staffName}</td>
                            <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontSize: '0.95rem', fontWeight: '700', color: '#1e40af' }}>{r.redmondHours.toFixed(2)}</td>
                            <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontSize: '0.95rem', fontWeight: '700', color: '#0e7490' }}>{r.kirklandHours.toFixed(2)}</td>
                            <td style={{ padding: '0.85rem 1rem', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>{r.schoolShifts}</td>
                            <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontSize: '0.95rem', fontWeight: '700', color: '#6d28d9' }}>{r.schoolHours.toFixed(2)}</td>
                            <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontSize: '1rem', fontWeight: '800', color: '#059669' }}>{r.totalPaid.toFixed(2)}</td>
                            <td style={{ padding: '0.85rem 1rem', fontSize: '0.72rem', color: '#dc2626' }}>
                              {r.flags.length > 0 ? r.flags.join(' · ') : <span style={{ color: '#9ca3af' }}>—</span>}
                            </td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f0fdf4' }}>
                          <td style={{ padding: '0.85rem 1rem', fontWeight: '800', fontSize: '0.875rem' }}>TOTAL</td>
                          <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '800', color: '#1e40af' }}>{sumBy('redmondHours').toFixed(2)}</td>
                          <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '800', color: '#0e7490' }}>{sumBy('kirklandHours').toFixed(2)}</td>
                          <td style={{ padding: '0.85rem 1rem', textAlign: 'center', fontWeight: '800', color: '#6b7280' }}>{sumBy('schoolShifts')}</td>
                          <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '800', color: '#6d28d9' }}>{sumBy('schoolHours').toFixed(2)}</td>
                          <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '800', color: '#059669', fontSize: '1rem' }}>{grandTotal.toFixed(2)}</td>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Break rules info */}
                <div style={{ ...styles.card, marginTop: '1rem', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe' }}>
                  <h3 style={{ fontSize: '0.8rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }}>⚙️ Break Rules Applied Automatically</h3>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.78rem', color: '#1e40af', lineHeight: 1.8 }}>
                    <li>Break 1 (≤10 min) — always free, no deduction</li>
                    <li>Break 2 (&gt;10 min) — deducted from paid hours</li>
                    <li>Break 2 ≤10 min + Break 3 exists — Break 3 is deducted</li>
                    <li>School/BCA shifts — actual hours from check-in to check-out</li>
                  </ul>
                </div>
              </div>
            )}

            {/* ── Detail Tab ── */}
            {activeTab === 'detail' && (
              <div style={styles.card}>
                <h2 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '1rem' }}>All Entries ({filteredEntries.length})</h2>
                {filteredEntries.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>No entries for this period.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          {['Date','Staff','Location','In','Out','Breaks','Total Hrs','Break Ded.','Paid Hrs','Status'].map(h => (
                            <th key={h} style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: '700', color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEntries.sort((a, b) => a.date.localeCompare(b.date)).map((entry, i) => {
                          const school = isSchoolLocation(entry.checkInLocation);
                          const deduction = school ? 0 : calcBreakDeduction(entry.breaks);
                          const paid = calcPaidHours(entry);
                          const noCheckout = !entry.checkOutTime;
                          return (
                            <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: noCheckout ? '#fef3c7' : i % 2 === 0 ? 'white' : '#f9fafb' }}>
                              <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem' }}>{parseFirestoreDate(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                              <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem', fontWeight: '600' }}>{entry.staffName}{entry.addedManually && <span style={{ fontSize: '0.65rem', color: '#f59e0b', marginLeft: '0.3rem' }}>manual</span>}</td>
                              <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.75rem' }}>
                                <span style={{ backgroundColor: school ? '#ede9fe' : '#dbeafe', color: school ? '#6d28d9' : '#1e40af', padding: '0.1rem 0.4rem', borderRadius: '3px', fontWeight: '600', fontSize: '0.7rem' }}>
                                  {school ? entry.checkInLocation?.toUpperCase() : CENTER_LABELS[classifyEntry(entry.checkInLocation, payrollMap).center]}
                                </span>
                              </td>
                              <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.78rem', color: '#6b7280' }}>{fmt12(entry.checkInTime)}</td>
                              <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.78rem', color: noCheckout ? '#dc2626' : '#6b7280' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  {noCheckout ? '⚠ Missing' : fmt12(entry.checkOutTime)}
                                  {entry.autoCheckedOut && <span style={{ fontSize: '0.6rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.05rem 0.3rem', borderRadius: '3px', fontWeight: '600' }}>auto</span>}
                                  <button onClick={() => {
  setEditEntry(entry);
  let prefill = '';
  if (entry.checkOutTime) {
    const t = entry.checkOutTime?.toDate ? entry.checkOutTime.toDate() : new Date(entry.checkOutTime);
    prefill = `${String(t.getHours()).padStart(2,'0')}:${String(t.getMinutes()).padStart(2,'0')}`;
  }
  setEditForm({ checkOut: prefill, notes: entry.adminNotes || '' });
}} style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem', backgroundColor: '#eff6ff', color: '#3b82f6', border: '1px solid #bfdbfe', borderRadius: '3px', cursor: 'pointer', fontWeight: '600' }}>
                                    Edit
                                  </button>
                                </div>
                              </td>
                              <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.78rem', textAlign: 'center', color: '#6b7280' }}>{entry.breaks?.length || 0}</td>
                              <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.78rem', textAlign: 'right' }}>{entry.totalHours?.toFixed(2) || '0'}</td>
                              <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.78rem', textAlign: 'right', color: deduction > 0 ? '#dc2626' : '#9ca3af' }}>{deduction > 0 ? `-${deduction.toFixed(2)}` : '—'}</td>
                              <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.85rem', textAlign: 'right', fontWeight: '700', color: '#059669' }}>{paid.toFixed(2)}</td>
                              <td style={{ padding: '0.65rem 0.75rem' }}>
                                <span style={{ fontSize: '0.7rem', fontWeight: '600', padding: '0.15rem 0.4rem', borderRadius: '4px', backgroundColor: entry.status === 'checked-out' ? '#d1fae5' : '#fef3c7', color: entry.status === 'checked-out' ? '#065f46' : '#92400e' }}>
                                  {entry.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ── Schedule Check Tab ── */}
        {!isLoading && activeTab === 'schedule' && (() => {
          const now = new Date();
          const todayKey = (() => {
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const d = String(now.getDate()).padStart(2, '0');
            return `${m}-${d}-${now.getFullYear()}`;
          })();

          const parse12 = (timeStr) => {
            if (!timeStr) return null;
            const upper = timeStr.toUpperCase().trim();
            const isPM = upper.includes('PM');
            const isAM = upper.includes('AM');
            const clean = upper.replace('AM','').replace('PM','').trim();
            const [h, m] = clean.split(':').map(Number);
            if (isNaN(h)) return null;
            let hours = h;
            if (isPM && h !== 12) hours += 12;
            if (isAM && h === 12) hours = 0;
            return hours * 60 + (m || 0);
          };

          // Group schedules by date
          const byDate = {};
          schedules.forEach(s => {
            if (!byDate[s.date]) byDate[s.date] = [];
            byDate[s.date].push(s);
          });

          // Group time entries by date + staffId
          const entryMap = {};
          timeEntries.forEach(e => {
            const key = `${e.date}__${e.staffId}`;
            if (!entryMap[key]) entryMap[key] = [];
            entryMap[key].push(e);
          });

          // Collect all dates that have either a schedule or a time entry
          const allDates = new Set([
            ...Object.keys(byDate),
            ...timeEntries.map(e => e.date)
          ]);
          const sortedDates = Array.from(allDates).sort();

          const nowMins = now.getHours() * 60 + now.getMinutes();

          // Today's missing: scheduled, shift started, no check-in
          const todaySchedules = byDate[todayKey] || [];
          const missingToday = todaySchedules.filter(s => {
            const startMins = parse12(s.startTime);
            if (startMins === null || startMins > nowMins) return false;
            return !entryMap[`${todayKey}__${s.staffId}`];
          });

          return (
            <div>
              {/* Today's alert */}
              {missingToday.length > 0 && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', backgroundColor: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: '10px' }}>
                  <div style={{ fontWeight: '800', color: '#dc2626', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    ⚠️ {missingToday.length} staff scheduled today but not checked in
                  </div>
                  {missingToday.map(s => (
                    <div key={s.id} style={{ fontSize: '0.82rem', color: '#dc2626', display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.25rem' }}>
                      <span style={{ fontWeight: '600' }}>{s.staffName}</span>
                      <span style={{ color: '#9ca3af' }}>·</span>
                      <span>{s.programType} · {s.startTime}–{s.endTime}</span>
                      <span style={{ backgroundColor: '#fee2e2', padding: '0.1rem 0.4rem', borderRadius: '3px', fontWeight: '700', fontSize: '0.72rem' }}>NO CHECK-IN</span>
                    </div>
                  ))}
                </div>
              )}
              {missingToday.length === 0 && todaySchedules.length > 0 && (
                <div style={{ marginBottom: '1.5rem', padding: '0.85rem 1.25rem', backgroundColor: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: '10px', color: '#15803d', fontWeight: '700', fontSize: '0.875rem' }}>
                  ✅ All scheduled staff have checked in today
                </div>
              )}

              {/* Per-day unified list */}
              <div style={styles.card}>
                <h2 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Schedule vs Check-in — {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </h2>

                {sortedDates.length === 0 ? (
                  <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No data found for this period.</p>
                ) : (
                  sortedDates.map(dateKey => {
                    const daySchedules = byDate[dateKey] || [];
                    const [m, d2, y] = dateKey.split('-').map(Number);
                    const dayLabel = new Date(y, m - 1, d2).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    const isToday = dateKey === todayKey;

                    // Build unified rows for this day
                    const rows = [];

                    // 1. All scheduled shifts
                    daySchedules.forEach(s => {
                      const hasEntry = entryMap[`${dateKey}__${s.staffId}`];
                      const startMins = parse12(s.startTime);
                      const shiftStarted = startMins !== null && (isToday ? startMins <= nowMins : true);
                      const isFuture = !shiftStarted && isToday;

                      let status, statusBg, statusColor;
                      if (hasEntry) {
                        status = '✅ Checked in'; statusBg = '#d1fae5'; statusColor = '#065f46';
                      } else if (isFuture) {
                        status = '⏳ Upcoming'; statusBg = '#f3f4f6'; statusColor = '#6b7280';
                      } else {
                        status = '⚠ No check-in'; statusBg = '#fee2e2'; statusColor = '#dc2626';
                      }

                      rows.push({ key: s.id, staffName: s.staffName, time: `${s.startTime}–${s.endTime}`, program: s.programType, entry: hasEntry ? hasEntry[0] : null, status, statusBg, statusColor });
                    });

                    // 2. Unscheduled check-ins — time entries with no matching schedule
                    const scheduledStaffIds = new Set(daySchedules.map(s => s.staffId));
                    Object.entries(entryMap).forEach(([key, entries]) => {
                      if (!key.startsWith(dateKey + '__')) return;
                      const staffId = key.split('__')[1];
                      if (scheduledStaffIds.has(staffId)) return; // already in scheduled list
                      const entry = entries[0];
                      rows.push({
                        key: `unscheduled-${key}`,
                        staffName: entry.staffName,
                        time: null,
                        program: null,
                        entry,
                        status: '🔔 Unscheduled',
                        statusBg: '#fef3c7',
                        statusColor: '#92400e',
                      });
                    });

                    if (rows.length === 0) return null;

                    return (
                      <div key={dateKey} style={{ marginBottom: '1.25rem' }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: '800', color: isToday ? '#2563eb' : '#374151', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {dayLabel} {isToday && <span style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.68rem' }}>Today</span>}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                          {rows.map(row => (
                            <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', backgroundColor: 'white', border: '1px solid #f3f4f6', borderRadius: '7px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: '600', fontSize: '0.82rem', minWidth: '120px' }}>{row.staffName}</span>
                              {row.time && <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{row.time}</span>}
                              {row.program && <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{row.program}</span>}
                              {row.entry && (
                                <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                                  in {row.entry.checkInTime?.toDate().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) || '?'}
                                  {row.entry.checkOutTime && ` → out ${row.entry.checkOutTime.toDate().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
                                </span>
                              )}
                              <span style={{ marginLeft: 'auto', fontSize: '0.72rem', fontWeight: '700', backgroundColor: row.statusBg, color: row.statusColor, padding: '0.15rem 0.5rem', borderRadius: '4px' }}>
                                {row.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Add Missed Shift Modal ── */}
      {showMissedShift && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.75rem', width: '90%', maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>Add Missed Shift</h2>
              <button onClick={() => setShowMissedShift(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={styles.label}>Staff Member *</label>
                <select value={missedForm.staffId} onChange={e => setMissedForm(f => ({ ...f, staffId: e.target.value }))} style={styles.input}>
                  <option value="">Select staff...</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Date *</label>
                <input type="date" value={missedForm.date} onChange={e => setMissedForm(f => ({ ...f, date: e.target.value }))} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Location *</label>
                <select value={missedForm.location} onChange={e => setMissedForm(f => ({ ...f, location: e.target.value }))} style={styles.input}>
                  <option value="redmond">Stemtree Redmond</option>
                  <option value="kirkland">Stemtree Kirkland</option>
                  {schoolLocations.map(loc => (
                    <option key={loc.id} value={locationKeyFromName(loc.name)}>
                      {loc.name} ({CENTER_LABELS[getPayrollCenter(loc)]} payroll)
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={styles.label}>Check-in Time *</label>
                  <input type="time" value={missedForm.checkIn} onChange={e => setMissedForm(f => ({ ...f, checkIn: e.target.value }))} style={styles.input} />
                </div>
                <div>
                  <label style={styles.label}>Check-out Time *</label>
                  <input type="time" value={missedForm.checkOut} onChange={e => setMissedForm(f => ({ ...f, checkOut: e.target.value }))} style={styles.input} />
                </div>
              </div>
              {classifyEntry(missedForm.location, payrollMap).isSchool && (
                <div style={{ padding: '0.75rem', backgroundColor: '#ede9fe', borderRadius: '8px', fontSize: '0.8rem', color: '#6d28d9', fontWeight: '600' }}>
                  School shift — actual hours will be calculated from check-in/check-out times.
                </div>
              )}
              <div>
                <label style={styles.label}>Notes (optional)</label>
                <input type="text" value={missedForm.notes} onChange={e => setMissedForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. forgot to check in" style={styles.input} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button onClick={() => setShowMissedShift(false)} style={{ ...styles.button, flex: 1, backgroundColor: '#6b7280' }}>Cancel</button>
              <button onClick={handleSaveMissedShift} disabled={savingMissed} style={{ ...styles.button, flex: 1, backgroundColor: savingMissed ? '#9ca3af' : '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <Save size={16} /> {savingMissed ? 'Saving...' : 'Save Shift'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Edit Entry Modal ── */}
      {editEntry && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.75rem', width: '90%', maxWidth: '420px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>Edit Checkout — {editEntry.staffName}</h2>
              <button onClick={() => setEditEntry(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
              Date: {editEntry.date} · Check-in: {fmt12(editEntry.checkInTime)}
              {editEntry.autoCheckedOut && <span style={{ marginLeft: '0.5rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.1rem 0.4rem', borderRadius: '3px', fontWeight: '600', fontSize: '0.72rem' }}>Auto-checked out</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={styles.label}>Correct Check-out Time *</label>
                <input type="time" value={editForm.checkOut} onChange={e => setEditForm(f => ({ ...f, checkOut: e.target.value }))} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Admin Notes (optional)</label>
                <input type="text" value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. forgot to check out" style={styles.input} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button onClick={() => setEditEntry(null)} style={{ ...styles.button, flex: 1, backgroundColor: '#6b7280' }}>Cancel</button>
              <button onClick={handleSaveEdit} disabled={savingEdit} style={{ ...styles.button, flex: 1, backgroundColor: savingEdit ? '#9ca3af' : '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <Save size={16} /> {savingEdit ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StaffReports;